/**
 * useSavedCommunities Hook
 * Fetches and manages saved communities for current user
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { getSavedCommunities, toggleSavedCommunity } from "../services/saved-communities.service"

interface UseSavedCommunitiesOptions {
  page?: number
  limit?: number
  search?: string
  enabled?: boolean
}

/**
 * Hook for fetching saved communities
 */
export function useSavedCommunities(options?: UseSavedCommunitiesOptions) {
  return useQuery({
    queryKey: ["saved-communities", options?.page, options?.limit, options?.search],
    queryFn: () =>
      getSavedCommunities({
        page: options?.page,
        limit: options?.limit,
        search: options?.search,
      }),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook for toggling community save status
 * Invalidates saved communities list, profile statistics, and related queries on success
 */
export function useToggleCommunitySave() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (communityId: string) => toggleSavedCommunity(communityId),
    onSuccess: (_data, variables) => {
      // Invalidate saved communities list
      queryClient.invalidateQueries({
        queryKey: ["saved-communities"],
      })

      // Invalidate profile statistics (saved communities count)
      queryClient.invalidateQueries({
        queryKey: ["profileStatistics"],
      })
      queryClient.invalidateQueries({
        queryKey: ["publicProfileStatistics"],
      })

      // Invalidate community detail queries
      queryClient.invalidateQueries({
        queryKey: ["community", variables],
      })

      // Invalidate user communities list
      queryClient.invalidateQueries({
        queryKey: ["userCommunities"],
      })
    },
  })
}
